import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SearchCommandPalette from '../../components/ui/SearchCommandPalette';
import { NotificationProvider, useNotification } from '../../components/ui/NotificationToast';
import { projectsService } from '../../services/dataService';
import { t } from '../../utils/translations';

// Import page components
import ProjectCard from './components/ProjectCard';
import ProjectFilters from './components/ProjectFilters';
import ProjectTable from './components/ProjectTable';
import BulkActions from './components/BulkActions';
import ProjectStats from './components/ProjectStats';
import StatusUpdateModal from './components/StatusUpdateModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

const ProjectsManagementContent = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  const { success, error, warning } = useNotification();

  // Projects data state
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch projects from database
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const data = await projectsService.getAll();
        
        // Map database response to component expected format
        const mappedProjects = data?.map((project) => ({
          id: project?.id,
          title: project?.titleAr || project?.titleEn || project?.titleTr || t('common.untitled'),
          description: project?.descriptionAr || project?.descriptionEn || project?.descriptionTr || '',
          status: project?.status || 'planned',
          progress: project?.progress || 0,
          category: project?.categoryAr || project?.categoryEn || project?.categoryTr || 'عام',
          startDate: project?.startDate || '',
          endDate: project?.endDate || '',
          memberCount: project?.memberCount || 0,
          image: project?.imageUrl || '',
          imageAlt: `${project?.titleEn || project?.titleAr || 'Project'} image`,
          createdAt: project?.createdAt || new Date().toISOString(),
          // Keep original fields for updates
          titleAr: project?.titleAr,
          titleEn: project?.titleEn,
          titleTr: project?.titleTr,
          descriptionAr: project?.descriptionAr,
          descriptionEn: project?.descriptionEn,
          descriptionTr: project?.descriptionTr,
          categoryAr: project?.categoryAr,
          categoryEn: project?.categoryEn,
          categoryTr: project?.categoryTr,
          imageUrl: project?.imageUrl
        }));

        setProjects(mappedProjects);
      } catch (err) {
        console.error('Error fetching projects:', err);
        error(t('projects.failedToLoad') || 'فشل في تحميل المشاريع. يرجى المحاولة مرة أخرى.');
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [error]);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    category: 'all',
    progressMin: 0,
    progressMax: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    startDateFrom: '',
    startDateTo: ''
  });

  // Filter and sort projects
  const filteredProjects = projects?.filter((project) => {
    const matchesSearch = !filters?.search ||
    project?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
    project?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase());

    const matchesStatus = filters?.status === 'all' || project?.status === filters?.status;
    const matchesCategory = filters?.category === 'all' || project?.category === filters?.category;
    const matchesProgress = project?.progress >= filters?.progressMin && project?.progress <= filters?.progressMax;

    let matchesDateRange = true;
    if (filters?.startDateFrom) {
      matchesDateRange = matchesDateRange && new Date(project.startDate) >= new Date(filters.startDateFrom);
    }
    if (filters?.startDateTo) {
      matchesDateRange = matchesDateRange && new Date(project.startDate) <= new Date(filters.startDateTo);
    }

    return matchesSearch && matchesStatus && matchesCategory && matchesProgress && matchesDateRange;
  })?.sort((a, b) => {
    const aValue = a?.[filters?.sortBy];
    const bValue = b?.[filters?.sortBy];

    if (filters?.sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
        e?.preventDefault();
        setIsSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Project actions
  const handleCreateProject = () => {
    window.location.href = '/project-form';
  };

  const handleEditProject = (project) => {
    window.location.href = `/project-form?id=${project?.id}`;
  };

  const handleViewProject = (project) => {
    success(`عرض تفاصيل المشروع: ${project?.title}`);
  };

  const handleStatusUpdate = (project) => {
    setSelectedProject(project);
    setIsStatusModalOpen(true);
  };

  const handleStatusUpdateSubmit = async (projectId, updateData) => {
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      // Map component format to service format
      const updatePayload = {
        titleAr: project.titleAr,
        titleEn: project.titleEn,
        titleTr: project.titleTr,
        descriptionAr: project.descriptionAr,
        descriptionEn: project.descriptionEn,
        descriptionTr: project.descriptionTr,
        categoryAr: project.categoryAr,
        categoryEn: project.categoryEn,
        categoryTr: project.categoryTr,
        imageUrl: project.image,
        status: updateData.status,
        progress: updateData.progress || 0,
        memberCount: project.memberCount || 0,
        startDate: project.startDate,
        endDate: project.endDate
      };

      await projectsService.update(projectId, updatePayload);
      
      // Refresh projects list
      const data = await projectsService.getAll();
      const mappedProjects = data?.map((proj) => ({
        id: proj?.id,
        title: proj?.titleAr || proj?.titleEn || proj?.titleTr || t('common.untitled'),
        description: proj?.descriptionAr || proj?.descriptionEn || proj?.descriptionTr || '',
        status: proj?.status || 'planned',
        progress: proj?.progress || 0,
        category: proj?.categoryAr || proj?.categoryEn || proj?.categoryTr || 'عام',
        startDate: proj?.startDate || '',
        endDate: proj?.endDate || '',
        memberCount: proj?.memberCount || 0,
        image: proj?.imageUrl || '',
        imageAlt: `${proj?.titleEn || proj?.titleAr || 'Project'} image`,
        createdAt: proj?.createdAt || new Date().toISOString(),
        // Keep original mapped fields for compatibility
        titleAr: proj?.titleAr,
        titleEn: proj?.titleEn,
        titleTr: proj?.titleTr,
        descriptionAr: proj?.descriptionAr,
        descriptionEn: proj?.descriptionEn,
        descriptionTr: proj?.descriptionTr,
        categoryAr: proj?.categoryAr,
        categoryEn: proj?.categoryEn,
        categoryTr: proj?.categoryTr,
        imageUrl: proj?.imageUrl
      }));
      
      setProjects(mappedProjects);
      success(`تم تحديث حالة المشروع بنجاح`);
      setIsStatusModalOpen(false);
      setSelectedProject(null);
    } catch (err) {
      console.error('Error updating project status:', err);
      error(t('projects.failedToUpdate') || 'فشل في تحديث حالة المشروع');
    }
  };

  // Selection handlers
  const handleSelectProject = (projectId, isSelected) => {
    if (isSelected) {
      setSelectedProjects((prev) => [...prev, projectId]);
    } else {
      setSelectedProjects((prev) => prev?.filter((id) => id !== projectId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedProjects(filteredProjects?.map((p) => p?.id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedProjects([]);
  };

  // Bulk actions
  const handleBulkStatusUpdate = async (status) => {
    try {
      await Promise.all(
        selectedProjects.map(id => {
          const project = projects.find(p => p.id === id);
          if (!project) return Promise.resolve();

          const updatePayload = {
            titleAr: project.titleAr,
            titleEn: project.titleEn,
            titleTr: project.titleTr,
            descriptionAr: project.descriptionAr,
            descriptionEn: project.descriptionEn,
            descriptionTr: project.descriptionTr,
            categoryAr: project.categoryAr,
            categoryEn: project.categoryEn,
            categoryTr: project.categoryTr,
            imageUrl: project.imageUrl || project.image,
            status: status,
            progress: project.progress || 0,
            memberCount: project.memberCount || 0,
            startDate: project.startDate,
            endDate: project.endDate
          };

          return projectsService.update(id, updatePayload);
        })
      );

      // Refresh projects list
      const data = await projectsService.getAll();
      const mappedProjects = data?.map((proj) => ({
        id: proj?.id,
        title: proj?.titleAr || proj?.titleEn || proj?.titleTr || t('common.untitled'),
        description: proj?.descriptionAr || proj?.descriptionEn || proj?.descriptionTr || '',
        status: proj?.status || 'planned',
        progress: proj?.progress || 0,
        category: proj?.categoryAr || proj?.categoryEn || proj?.categoryTr || 'عام',
        startDate: proj?.startDate || '',
        endDate: proj?.endDate || '',
        memberCount: proj?.memberCount || 0,
        image: proj?.imageUrl || '',
        imageAlt: `${proj?.titleEn || proj?.titleAr || 'Project'} image`,
        createdAt: proj?.createdAt || new Date().toISOString(),
        titleAr: proj?.titleAr,
        titleEn: proj?.titleEn,
        titleTr: proj?.titleTr,
        descriptionAr: proj?.descriptionAr,
        descriptionEn: proj?.descriptionEn,
        descriptionTr: proj?.descriptionTr,
        categoryAr: proj?.categoryAr,
        categoryEn: proj?.categoryEn,
        categoryTr: proj?.categoryTr,
        imageUrl: proj?.imageUrl
      }));
      
      setProjects(mappedProjects);
      success(`تم تحديث حالة ${selectedProjects?.length} مشروع إلى "${status}"`);
      setSelectedProjects([]);
    } catch (err) {
      console.error('Error updating projects status:', err);
      error(t('projects.failedToUpdate') || 'فشل في تحديث حالة المشاريع');
    }
  };

  const handleBulkExport = (format) => {
    // Export functionality (client-side)
    const exportData = projects.filter(p => selectedProjects.includes(p.id));
    console.log('Exporting projects:', exportData);
    success(`تم تصدير ${selectedProjects?.length} مشروع بصيغة ${format?.toUpperCase()}`);
    setSelectedProjects([]);
  };

  const handleDeleteProject = (project) => {
    setProjectToDelete(project);
    setIsBulkDelete(false);
    setIsDeleteModalOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedProjects?.length === 0) {
      warning('يرجى تحديد مشاريع للحذف');
      return;
    }
    setProjectToDelete(null);
    setIsBulkDelete(true);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (isBulkDelete) {
        // Delete multiple projects
        await Promise.all(selectedProjects.map(id => projectsService.delete(id)));
        const count = selectedProjects.length;
        setSelectedProjects([]);
        
        // Refresh projects list
        const data = await projectsService.getAll();
        const mappedProjects = data?.map((proj) => ({
          id: proj?.id,
          title: proj?.titleAr || proj?.titleEn || proj?.titleTr || t('common.untitled'),
          description: proj?.descriptionAr || proj?.descriptionEn || proj?.descriptionTr || '',
          status: proj?.status || 'planned',
          progress: proj?.progress || 0,
          category: proj?.categoryAr || proj?.categoryEn || proj?.categoryTr || 'عام',
          startDate: proj?.startDate || '',
          endDate: proj?.endDate || '',
          memberCount: proj?.memberCount || 0,
          image: proj?.imageUrl || '',
          imageAlt: `${proj?.titleEn || proj?.titleAr || 'Project'} image`,
          createdAt: proj?.createdAt || new Date().toISOString(),
          titleAr: proj?.titleAr,
          titleEn: proj?.titleEn,
          titleTr: proj?.titleTr,
          descriptionAr: proj?.descriptionAr,
          descriptionEn: proj?.descriptionEn,
          descriptionTr: proj?.descriptionTr,
          categoryAr: proj?.categoryAr,
          categoryEn: proj?.categoryEn,
          categoryTr: proj?.categoryTr,
          imageUrl: proj?.imageUrl
        }));
        
        setProjects(mappedProjects);
        success(`تم حذف ${count} مشروع بنجاح`);
      } else if (projectToDelete) {
        // Delete single project
        await projectsService.delete(projectToDelete.id);
        
        // Refresh projects list
        const data = await projectsService.getAll();
        const mappedProjects = data?.map((proj) => ({
          id: proj?.id,
          title: proj?.titleAr || proj?.titleEn || proj?.titleTr || t('common.untitled'),
          description: proj?.descriptionAr || proj?.descriptionEn || proj?.descriptionTr || '',
          status: proj?.status || 'planned',
          progress: proj?.progress || 0,
          category: proj?.categoryAr || proj?.categoryEn || proj?.categoryTr || 'عام',
          startDate: proj?.startDate || '',
          endDate: proj?.endDate || '',
          memberCount: proj?.memberCount || 0,
          image: proj?.imageUrl || '',
          imageAlt: `${proj?.titleEn || proj?.titleAr || 'Project'} image`,
          createdAt: proj?.createdAt || new Date().toISOString(),
          titleAr: proj?.titleAr,
          titleEn: proj?.titleEn,
          titleTr: proj?.titleTr,
          descriptionAr: proj?.descriptionAr,
          descriptionEn: proj?.descriptionEn,
          descriptionTr: proj?.descriptionTr,
          categoryAr: proj?.categoryAr,
          categoryEn: proj?.categoryEn,
          categoryTr: proj?.categoryTr,
          imageUrl: proj?.imageUrl
        }));
        
        setProjects(mappedProjects);
        success('تم حذف المشروع بنجاح');
      }
      
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
      setIsBulkDelete(false);
    } catch (err) {
      console.error('Error deleting project:', err);
      error(t('projects.failedToDelete') || 'فشل في حذف المشروع');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen} />

      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <main className={`transition-all duration-300 ${
      isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pt-16`
      }>
        <div className="p-6">
          {/* Breadcrumbs */}
          <Breadcrumbs className="mb-6" />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                إدارة المشاريع
              </h1>
              <p className="text-muted-foreground">
                إدارة وتتبع مشاريع الاتحاد مع مراقبة التقدم والحالة
              </p>
            </div>

            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSearchOpen(true)}
                iconName="Search"
                iconPosition="left"
                iconSize={16}>

                بحث (Ctrl+K)
              </Button>

              <div className="flex items-center bg-muted rounded-lg p-1">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  iconName="Grid3X3"
                  iconSize={16}
                  title="عرض البطاقات" />

                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  iconName="List"
                  iconSize={16}
                  title="عرض الجدول" />

              </div>

              <Button
                variant="default"
                onClick={handleCreateProject}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}>

                مشروع جديد
              </Button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-muted rounded w-1/3 mx-auto"></div>
                <div className="h-32 bg-muted rounded"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-48 bg-muted rounded"></div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Statistics */}
              <ProjectStats projects={filteredProjects} />

          {/* Filters */}
          <ProjectFilters
            filters={filters}
            onFiltersChange={setFilters}
            totalCount={projects?.length}
            filteredCount={filteredProjects?.length} />


          {/* Projects Display */}
          {viewMode === 'cards' ?
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects?.map((project) =>
            <ProjectCard
              key={project?.id}
              project={project}
              onEdit={handleEditProject}
              onView={handleViewProject}
              onStatusUpdate={handleStatusUpdate}
              onDelete={handleDeleteProject}
              isSelected={selectedProjects?.includes(project?.id)}
              onSelect={handleSelectProject} />

            )}
            </div> :

          <ProjectTable
            projects={filteredProjects}
            selectedProjects={selectedProjects}
            onSelectAll={handleSelectAll}
            onSelectProject={handleSelectProject}
            onEdit={handleEditProject}
            onView={handleViewProject}
            onStatusUpdate={handleStatusUpdate}
            onDelete={handleDeleteProject} />

          }

          {/* Empty State */}
          {filteredProjects?.length === 0 &&
          <div className="text-center py-12">
              <Icon name="FolderOpen" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                لا توجد مشاريع
              </h3>
              <p className="text-muted-foreground mb-6">
                {filters?.search || filters?.status !== 'all' || filters?.category !== 'all' ? 'لم يتم العثور على مشاريع تطابق معايير البحث' : 'ابدأ بإنشاء مشروعك الأول'
              }
              </p>
              <Button
              variant="default"
              onClick={handleCreateProject}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}>

                إنشاء مشروع جديد
              </Button>
            </div>
          }
            </>
          )}
        </div>
      </main>
      {/* Bulk Actions */}
      <BulkActions
        selectedCount={selectedProjects?.length}
        onBulkStatusUpdate={handleBulkStatusUpdate}
        onBulkExport={handleBulkExport}
        onBulkDelete={handleBulkDelete}
        onClearSelection={handleClearSelection} />

      {/* Status Update Modal */}
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        project={selectedProject}
        onUpdate={handleStatusUpdateSubmit} />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProjectToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={handleConfirmDelete}
        project={projectToDelete}
        isBulkDelete={isBulkDelete}
        selectedCount={selectedProjects?.length} />

      {/* Quick Action Button */}
      <QuickActionButton />
      {/* Search Command Palette */}
      <SearchCommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)} />

    </div>);

};

const ProjectsManagement = () => {
  return (
    <NotificationProvider>
      <ProjectsManagementContent />
    </NotificationProvider>);

};

export default ProjectsManagement;